/**
 * Sheet 40, header parameters (mission 6 WP05 T034; FR-022, US11; GEN.3,
 * GEN.6, TEN.4, D.3, A.5; research §1.23; contracts/ui-api.md "Header
 * parameters (sheet 40)"; callouts 6–12).
 *
 * Full edit, replace-only, no reveal (DM-01M3BGTA0D7JMNSAMXC12E16MT):
 * - the three reporter fields are plain inputs holding the stored values
 *   (not secrets), and every PUT carries all three: an omitted one is cleared;
 * - a stored identifier is the API's mask in mono, rendered as given, including
 *   the 5-character Experian mask of dots plus its last 2
 *   (DM-01M3C8MKBH360E5HRVH5KXF9TP), with Replace and Clear. The PUT carries an
 *   identifier only when replaced (a string, upper-cased as typed) or cleared
 *   (`null`); an absent key keeps the stored value. The mask never enters form
 *   state, so it is never sent and never sits in an input (TEN.4, NFR-006);
 * - the Replace input is controlled by `changed[bureau]`, and a successful save
 *   resets `changed`, which unmounts it: no `.value` keeps the typed
 *   identifier (S12, R13). Spellcheck and autofill are off on every input.
 *
 * A status per bureau (Entered, or Not entered with the GEN.6 sentence, §2.6's
 * neutral badge), the storage statement and the static-versus-per-run line
 * (register 15), the provenance line from `updated_at` (linked to the audit
 * log from mission 10), and the sticky save bar only while the form is dirty (§8
 * "Settings"). A `validation-failed` answer renders each message verbatim
 * under its field; anything else, or an error for a field not on screen,
 * renders through ProblemBand. No Member view and no reveal (C-008, C-013).
 *
 * Mission 7 WP11 (T060; FR-016; sheet 40 callout 8; TX.2; squad E14): the page
 * also reads `bureausQuery()`, and a bureau whose effective credential status
 * is `verified` shows `BureauStatusBadge` Verified in place of Entered or Not
 * entered. A failed or pending bureaus read changes nothing and renders no
 * band. Bureau names come from `bureauName`, the one display map.
 *
 * Mission 10 WP18 T109 (FR-019 40/10; A.5): the provenance line is `HeaderAuditLink`, a link to the audit log
 * filtered to header-parameter changes.
 */
import { useId, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CircleAlert } from 'lucide-react'

import { Band } from '@/components/trellis/Band'
import { BureauStatusBadge } from '@/components/trellis/BureauStatusBadge'
import { MaskedValue } from '@/components/trellis/MaskedValue'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError, type Problem } from '@/lib/api'
import { bureauName } from '@/lib/api/bureaus'
import {
  BUREAUS,
  headerParamsBody,
  putHeaderParams,
  type Bureau,
  type HeaderParams,
  type ProgramIdentifier,
} from '@/lib/api/headerParams'
import { PROBLEM, fieldErrors } from '@/lib/auth'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { bureausQuery, headerParamsQuery, keys } from '@/lib/queries'
import { copyText } from '@/lib/provisional-copy'
import { HeaderAuditLink } from './HeaderAuditLink'
import { SettingsLayout } from './SettingsLayout'

type ReporterField = 'reporter_name' | 'reporter_address' | 'reporter_telephone'
type Reporter = Record<ReporterField, string>
/** A bureau key exists only once replaced (a string) or cleared (`null`). */
type Changed = Partial<Record<Bureau, string | null>>
type Mode = 'replace' | 'clear'

const REPORTER_FIELDS: ReadonlyArray<{ field: ReporterField; label: string }> = [
  { field: 'reporter_name', label: 'Reporter name' },
  { field: 'reporter_address', label: 'Reporter address' },
  { field: 'reporter_telephone', label: 'Reporter telephone' },
]

/** The contract's rule: Experian's identifier is exactly 5 characters, the others at most 10. */
const maxLength = (bureau: Bureau) => (bureau === 'experian' ? 5 : 10)

const identifierField = (bureau: Bureau) => `program_identifiers.${bureau}`

function reporterOf(p: HeaderParams): Reporter {
  return { reporter_name: p.reporter_name, reporter_address: p.reporter_address, reporter_telephone: p.reporter_telephone }
}

/** An error to render as a band: the API's problem, or register 16 for anything that is not one. */
type Failure = { problem: Problem } | { generic: true }

export default function HeaderParameters() {
  const query = useQuery(headerParamsQuery())

  return (
    <SettingsLayout>
      <h1 className="text-h1 text-navy-950">Header parameters</h1>
      {query.isPending ? (
        <>
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
          <Skeleton className="h-80 rounded-lg" />
        </>
      ) : query.isError ? (
        <FailureBand failure={failureOf(query.error)} />
      ) : (
        <HeaderForm stored={query.data} />
      )}
    </SettingsLayout>
  )
}

function failureOf(error: unknown): Failure {
  return error instanceof ApiError ? { problem: error.problem } : { generic: true }
}

function FailureBand({ failure }: { failure: Failure }) {
  return 'problem' in failure ? (
    <ProblemBand problem={failure.problem} />
  ) : (
    <Band kind="blocked">
      <p>{copyText('genericError')}</p>
    </Band>
  )
}

function HeaderForm({ stored }: { stored: HeaderParams }) {
  const queryClient = useQueryClient()
  const bureaus = useQuery(bureausQuery())
  const verified = new Set((bureaus.data?.bureaus ?? []).filter((b) => b.status === 'verified').map((b) => b.bureau))
  const formId = useId()
  const [edited, setEdited] = useState<Reporter>(() => reporterOf(stored))
  const [changed, setChanged] = useState<Changed>({})
  const [modes, setModes] = useState<Partial<Record<Bureau, Mode>>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<Failure | null>(null)

  const storedReporter = reporterOf(stored)
  const dirty =
    REPORTER_FIELDS.some(({ field }) => edited[field] !== storedReporter[field]) || Object.keys(changed).length > 0

  const showsInput = (bureau: Bureau) => !stored.program_identifiers[bureau].set || modes[bureau] !== undefined

  const gate = useBillingGate()
  const save = useMutation({
    mutationFn: () =>
      putHeaderParams(headerParamsBody({ ...edited, program_identifiers: changed }, Object.keys(changed) as Bureau[])),
    onSuccess: (body) => {
      queryClient.setQueryData(keys.headerParams, body)
      setEdited(reporterOf(body))
      setChanged({})
      setModes({})
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) {
        setFailure({ generic: true })
        return
      }
      const onScreen = new Set<string>([
        ...REPORTER_FIELDS.map((r) => r.field),
        ...BUREAUS.filter(showsInput).map(identifierField),
      ])
      const mapped: Record<string, string> = {}
      let unmapped = err.problem.type !== PROBLEM.VALIDATION_FAILED
      for (const { field, message } of fieldErrors(err)) {
        if (onScreen.has(field)) mapped[field] = message
        else unmapped = true
      }
      setErrors(mapped)
      if (unmapped || Object.keys(mapped).length === 0) setFailure({ problem: err.problem })
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    if (gate.refused) return
    setErrors({})
    setFailure(null)
    save.mutate()
  }

  function setIdentifier(bureau: Bureau, typed: string) {
    const value = typed.toUpperCase()
    setChanged((prev) => {
      const next = { ...prev }
      if (value !== '') next[bureau] = value
      else if (modes[bureau] === 'clear') next[bureau] = null
      else delete next[bureau]
      return next
    })
  }

  function replace(bureau: Bureau) {
    setModes((prev) => ({ ...prev, [bureau]: 'replace' }))
  }

  function clear(bureau: Bureau) {
    setModes((prev) => ({ ...prev, [bureau]: 'clear' }))
    setChanged((prev) => ({ ...prev, [bureau]: null }))
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-2 text-body-sm text-navy-700">
          <p>{copyText('headerStorageStatement')}</p>
          <p>{copyText('staticVersusPerRun')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          {/* mission 6 WP13: handoff from WP05: the card title comes from the register. */}
          <CardTitle>{copyText('headerReporterCardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {REPORTER_FIELDS.map(({ field, label }) => {
            const id = `${formId}-${field}`
            const error = errors[field]
            return (
              <div key={field} className="flex flex-col gap-1.5">
                <Label htmlFor={id}>{label}</Label>
                <Input
                  id={id}
                  value={edited[field]}
                  onChange={(e) => setEdited((prev) => ({ ...prev, [field]: e.target.value }))}
                  spellCheck={false}
                  autoComplete="off"
                  inputMode={field === 'reporter_telephone' ? 'tel' : undefined}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? `${id}-error` : undefined}
                />
                {error ? <FieldError id={`${id}-error`} message={error} /> : null}
              </div>
            )
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Program identifiers</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col divide-y divide-navy-200">
          {BUREAUS.map((bureau) => (
            <IdentifierRow
              key={bureau}
              bureau={bureau}
              id={`${formId}-${bureau}`}
              stored={stored.program_identifiers[bureau]}
              verified={verified.has(bureau)}
              input={showsInput(bureau)}
              autoFocus={modes[bureau] === 'replace'}
              value={changed[bureau] ?? ''}
              error={errors[identifierField(bureau)]}
              onChange={(v) => setIdentifier(bureau, v)}
              onReplace={() => replace(bureau)}
              onClear={() => clear(bureau)}
            />
          ))}
        </CardContent>
      </Card>

      {stored.updated_at ? (
        <p className="text-caption text-navy-600">
          <HeaderAuditLink updatedAt={stored.updated_at} />{' '}
          <span>{copyText('timesInUtc')}</span>
        </p>
      ) : null}

      {failure ? <FailureBand failure={failure} /> : null}

      {dirty ? (
        <div className="sticky bottom-0 -mx-[var(--content-padding)] flex h-14 items-center justify-end border-t border-navy-200 bg-white px-[var(--content-padding)]">
          {gate.refused ? (
            // Mission 9 WP14 T090 (amendment 8; U11, U30): the gated primary, demoted and relabeled.
            <Button type="submit" variant="secondary" {...gateButton(gate)}>
              {copyText('gatedReadOnly')}
            </Button>
          ) : (
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          )}
        </div>
      ) : null}
    </form>
  )
}

interface IdentifierRowProps {
  bureau: Bureau
  id: string
  stored: ProgramIdentifier
  /** The bureau's credential status is `verified` (sheet 41; TX.2). */
  verified: boolean
  input: boolean
  autoFocus: boolean
  value: string
  error: string | undefined
  onChange: (value: string) => void
  onReplace: () => void
  onClear: () => void
}

function IdentifierRow({ bureau, id, stored, verified, input, autoFocus, value, error, onChange, onReplace, onClear }: IdentifierRowProps) {
  const labelId = `${id}-label`
  const name = bureauName(bureau)
  return (
    <div data-bureau={bureau} className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        {input ? (
          <Label id={labelId} htmlFor={id}>
            {name}
          </Label>
        ) : (
          <span id={labelId} className="text-body-sm font-medium text-navy-950">
            {name}
          </span>
        )}
        {verified ? (
          <>
            <BureauStatusBadge status="verified" />
            {stored.set ? null : <span className="text-caption text-navy-600">{copyText('notEnteredGen6')}</span>}
          </>
        ) : stored.set ? (
          <Badge status="success">{copyText('entered')}</Badge>
        ) : (
          <>
            <Badge status="neutral">{copyText('notEntered')}</Badge>
            <span className="text-caption text-navy-600">{copyText('notEnteredGen6')}</span>
          </>
        )}
      </div>
      {input ? (
        <div className="flex flex-col gap-1.5">
          <Input
            id={id}
            className="max-w-60 font-mono"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            maxLength={maxLength(bureau)}
            spellCheck={false}
            autoComplete="off"
            autoFocus={autoFocus}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
          />
          {error ? <FieldError id={`${id}-error`} message={error} /> : null}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <MaskedValue value={stored.masked ?? ''} />
          <Button variant="secondary" size="sm" aria-describedby={labelId} onClick={onReplace}>
            Replace
          </Button>
          <Button variant="ghost" size="sm" aria-describedby={labelId} onClick={onClear}>
            Clear
          </Button>
        </div>
      )}
    </div>
  )
}

function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p id={id} className="flex items-center gap-1 text-caption text-[var(--color-error-700)]">
      <CircleAlert aria-hidden className="size-4 shrink-0" />
      {message}
    </p>
  )
}
